import React, { useState } from "react";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import {
    Button, Divider, Grid, IconButton, InputLabel, Link, List, ListItem, ListItemText, Typography
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useConveniosState } from "../context/convenios-context";
import { useConveniosController } from "../context/convenios-controller";
import { IDocumentoConvenioRequest } from "../../../../model/admin-portal/request/documento-convenio-request";
import { TextInputWithCounter } from "../../../components/text-input-with-counter";
import DefaultUploadFile from "../../../components/default-upload-file";
import AdminPortalAppService from "../../../../application/platform/admin-portal/admin-portal-app-service";
import { useBaseLayoutController } from "../../../base-layout/context/base-layout-controller";

const emptyDraft: IDocumentoConvenioRequest = { id: 0, descricao: '', dtaPublicacao: '', documentoUrl: '' };
const DOCUMENT_EXTENSIONS = ["pdf", "xls", "xlsx", "doc", "docx"];

export const ConvenioDocumentosSection = () => {
    const { dataRequest } = useConveniosState();
    const { saveDocumento, removeDocumento } = useConveniosController();
    const { showMessage } = useBaseLayoutController();
    const [draft, setDraft] = useState<IDocumentoConvenioRequest>(emptyDraft);
    const [editingIndex, setEditingIndex] = useState<number | undefined>(undefined);
    const [file, setFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);

    const handleEdit = (index: number) => {
        setDraft(dataRequest.documentos[index]);
        setFile(null);
        setEditingIndex(index);
    };

    const handleCancel = () => {
        setDraft(emptyDraft);
        setFile(null);
        setEditingIndex(undefined);
    };

    const handleFileSelected = async (selected: File | null) => {
        setFile(selected);
        if (!selected) return;

        try {
            setUploading(true);
            const result = await AdminPortalAppService.uploadDocumento(selected);
            const updatedDraft = { ...draft, documentoUrl: result.documentoUrl };
            if (editingIndex === undefined) {
                // Fluxo igual ao de Banners: assim que o upload termina, já entra na lista - sem clique extra
                saveDocumento(updatedDraft, undefined);
                handleCancel();
            } else {
                setDraft(updatedDraft);
            }
        } catch (error: any) {
            showMessage({ type: 'error', message: error?.response?.data?.message || 'Erro ao enviar arquivo' });
            setFile(null);
        } finally {
            setUploading(false);
        }
    };

    const handleSave = () => {
        if (!draft.descricao || !draft.dtaPublicacao || !draft.documentoUrl) return;
        saveDocumento(draft, editingIndex);
        handleCancel();
    };

    return (
        <Grid item lg={12} sm={12} xl={12}>
            <Typography variant="subtitle1" fontWeight="bold">Documentos</Typography>
            <Divider sx={{ mb: 1 }} />

            {dataRequest.documentos.length > 0 && (
                <List dense>
                    {dataRequest.documentos.map((doc, index) => (
                        <ListItem
                            key={index}
                            divider
                            secondaryAction={
                                <React.Fragment>
                                    <IconButton size="small" onClick={() => handleEdit(index)}><EditIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" onClick={() => removeDocumento(index)}><DeleteIcon fontSize="small" /></IconButton>
                                </React.Fragment>
                            }
                        >
                            <ListItemText
                                primary={doc.descricao}
                                secondary={
                                    <React.Fragment>
                                        {doc.dtaPublicacao ? new Date(doc.dtaPublicacao).toLocaleDateString('pt-BR') : ''}
                                        {doc.documentoUrl && (<React.Fragment> {' - '}<Link href={doc.documentoUrl} target="_blank" rel="noopener">abrir documento</Link></React.Fragment>)}
                                    </React.Fragment>
                                }
                            />
                        </ListItem>
                    ))}
                </List>
            )}

            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item lg={7} sm={8} xl={7}>
                        <InputLabel>Descrição *</InputLabel>
                        <TextInputWithCounter
                            size="small"
                            maxLength={100}
                            error={false}
                            value={draft.descricao}
                            onChange={(e) => setDraft({ ...draft, descricao: e.target.value })}
                            fullWidth
                        />
                    </Grid>
                    <Grid item lg={3} sm={4} xl={3}>
                        <InputLabel>Data Publicação *</InputLabel>
                        <DesktopDatePicker
                            format='DD/MM/YYYY'
                            slotProps={{ textField: { fullWidth: true, size: "small", error: false } }}
                            value={draft.dtaPublicacao ? dayjs(draft.dtaPublicacao) : null}
                            onChange={(value: Dayjs | null) => setDraft({ ...draft, dtaPublicacao: value?.toISOString() || '' })}
                        />
                    </Grid>
                    <Grid item lg={10} sm={12} xl={10}>
                        <InputLabel>Arquivo (PDF ou Excel) *</InputLabel>
                        <DefaultUploadFile
                            id="input-documento-convenio"
                            file={file}
                            setFileUpload={handleFileSelected}
                            extensions={DOCUMENT_EXTENSIONS}
                            maxFileSizeMb={10}
                            disabled={uploading || !draft.descricao || !draft.dtaPublicacao}
                        />
                        {(!draft.descricao || !draft.dtaPublicacao) && (
                            <Typography variant="caption" color="text.secondary">Preencha Descrição e Data antes de selecionar o arquivo</Typography>
                        )}
                        {uploading && (
                            <Typography variant="caption" color="text.secondary">Enviando arquivo, aguarde...</Typography>
                        )}
                        {editingIndex !== undefined && draft.documentoUrl && !uploading && (
                            <Typography variant="caption" color="text.secondary">
                                Arquivo atual: <Link href={draft.documentoUrl} target="_blank" rel="noopener">abrir documento</Link> — selecione um novo arquivo para substituir
                            </Typography>
                        )}
                    </Grid>
                    <Grid item lg={2} sm={12} xl={2} sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
                        <Button
                            variant="outlined"
                            size="small"
                            startIcon={editingIndex === undefined ? <AddIcon /> : undefined}
                            onClick={handleSave}
                            disabled={uploading || !draft.descricao || !draft.dtaPublicacao || !draft.documentoUrl}
                        >
                            {uploading ? 'Enviando...' : editingIndex === undefined ? 'Adicionar' : 'Salvar'}
                        </Button>
                        {editingIndex !== undefined && (
                            <Button size="small" onClick={handleCancel}>Cancelar</Button>
                        )}
                    </Grid>
                </Grid>
            </LocalizationProvider>
        </Grid>
    );
};
